import { useEffect, useRef, useState } from "react";
import { Pause, Play, SpeakerHigh, Stop } from "@phosphor-icons/react";

/** "Listen to this guide" — narrates guide sections aloud with the browser
 * speech engine (same engine as the Listen/Translate bar). Shows which
 * section is being read so early readers can follow along. */
export default function GuideListenButton({ sections, testid = "guide-listen", color = "#166534" }) {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [idx, setIdx] = useState(-1);
  const idxRef = useRef(0);
  const queueRef = useRef([]);
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;

  const items = (sections || []).filter((s) => s?.text);
  useEffect(() => () => { if (canSpeak) window.speechSynthesis.cancel(); }, [canSpeak]);

  if (!canSpeak || items.length === 0) return null;

  const speakNext = () => {
    const i = idxRef.current;
    if (i >= queueRef.current.length) { setState("idle"); setIdx(-1); return; }
    const u = new SpeechSynthesisUtterance(queueRef.current[i].text);
    u.lang = "en";
    u.rate = (() => { try { const v = parseFloat(localStorage.getItem("np:tts-rate")); return v >= 0.5 && v <= 2 ? v : 1; } catch { return 1; } })();
    u.onend = () => { idxRef.current = i + 1; setIdx(i + 1 < queueRef.current.length ? i + 1 : -1); speakNext(); };
    u.onerror = (e) => {
      // ignore expected/environmental errors (headless, autoplay policy, cancel)
      if (e?.error && !["canceled", "interrupted", "synthesis-failed", "not-allowed"].includes(e.error)) setState("idle");
    };
    window.speechSynthesis.speak(u);
  };

  const play = () => {
    window.speechSynthesis.cancel();
    queueRef.current = items.map((s) => ({ ...s, text: `${s.title}. ${s.text}` }));
    idxRef.current = 0;
    setIdx(0);
    setState("playing");
    setTimeout(speakNext, 40);
  };
  const toggle = () => {
    if (state === "playing") { window.speechSynthesis.pause(); setState("paused"); }
    else if (state === "paused") { window.speechSynthesis.resume(); setState("playing"); }
    else play();
  };
  const stop = () => { window.speechSynthesis.cancel(); setState("idle"); setIdx(-1); };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-wrap`}>
      <button onClick={toggle} data-testid={`${testid}-toggle`}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase border transition-colors hover:bg-black/[0.04]"
        style={{ color, borderColor: `${color}4D` }}>
        {state === "playing" ? <Pause size={13} weight="bold" /> : <Play size={13} weight="bold" />}
        {state === "playing" ? "Pause" : state === "paused" ? "Resume" : "Listen"}
      </button>
      {state !== "idle" && (
        <button onClick={stop} data-testid={`${testid}-stop`} aria-label="Stop narration"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase border border-black/10 text-black/50 hover:bg-black/[0.04]">
          <Stop size={12} weight="bold" /> Stop
        </button>
      )}
      {state !== "idle" && idx >= 0 && items[idx] && (
        <span className="inline-flex items-center gap-1.5 text-xs text-black/50" data-testid={`${testid}-progress`}>
          <SpeakerHigh size={13} weight="fill" style={{ color }} className={state === "playing" ? "animate-pulse" : ""} />
          Reading: {items[idx].title} ({idx + 1}/{items.length})
        </span>
      )}
    </div>
  );
}

/** Build narration sections from a guide content object (either shape). */
export function guideToSections(c) {
  if (!c) return [];
  const out = [];
  if (c.intro) out.push({ title: "Welcome", text: c.intro });
  if (c.highlights?.length) out.push({ title: "What you can do here", text: c.highlights.join(". ") });
  if (c.identification_tips?.length) out.push({ title: "How to explore like a scientist", text: c.identification_tips.join(". ") });
  if (c.tips?.length) out.push({ title: "Tips", text: c.tips.join(". ") });
  if (c.did_you_know?.length) out.push({ title: "Did you know", text: c.did_you_know.join(". ") });
  if (c.beginner_steps?.length) out.push({ title: "Your first adventure", text: c.beginner_steps.join(". ") });
  if (c.conservation_note) out.push({ title: "Why it matters", text: c.conservation_note });
  return out;
}
